import { apiGet, apiGetPage, apiSend } from './client'
import type { Paginated } from './client'
import type {
  TagView,
  Board,
  ProjectReport,
  ProjectTeam,
  ProjectView,
  ProjectVisibility,
  TaskKind,
  TaskView,
  TeamMember,
} from './types'

export function fetchProjects(includeArchived = false, signal?: AbortSignal): Promise<ProjectView[]> {
  return apiGet<ProjectView[]>('/project', { query: { include_archived: includeArchived }, signal })
}

/**
 * A project and its board in one response.
 *
 * The list and board views show the same tasks arranged differently, so they
 * share an endpoint - fetching separately would let the two disagree after an
 * edit in one of them.
 */
export function fetchBoard(projectId: string, includeCompleted = false, signal?: AbortSignal): Promise<Board> {
  return apiGet<Board>(`/project/${projectId}`, { query: { include_completed: includeCompleted }, signal })
}

export function fetchAssignableMembers(search?: string, signal?: AbortSignal): Promise<TeamMember[]> {
  return apiGet<TeamMember[]>('/project/assignable-member', {
    query: search ? { search } : {},
    signal,
  })
}

/** Resolves WEB-42 to its task, which is what a pasted link carries. */
export function fetchTaskByKey(key: string, signal?: AbortSignal): Promise<TaskView> {
  return apiGet<TaskView>(`/task/key/${encodeURIComponent(key)}`, { signal })
}

export function createProject(body: {
  name: string
  /** Suggested from the name by the server when omitted. */
  code?: string
  visibility?: ProjectVisibility
  description?: string
  colour?: string
  member_ids?: string[]
  team_ids?: string[]
}): Promise<Board | null> {
  return apiSend<Board>('/project', 'POST', body)
}

export function updateProject(
  projectId: string,
  body: {
    name: string
    code?: string
    visibility?: ProjectVisibility
    description?: string
    colour?: string
  },
): Promise<Board | null> {
  return apiSend<Board>(`/project/${projectId}`, 'PATCH', body)
}

export function setProjectArchived(projectId: string, archived: boolean): Promise<Board | null> {
  return apiSend<Board>(`/project/${projectId}/archive`, 'PUT', { archived })
}

export function deleteProject(projectId: string): Promise<null> {
  return apiSend<null>(`/project/${projectId}`, 'DELETE') as Promise<null>
}

/**
 * The project's own people, optionally narrowed by a search.
 *
 * Unlike the global directory this has no minimum search length: these people
 * are already visible on the project itself, so withholding them until
 * somebody types protects nothing and only makes assigning work harder.
 */
export function fetchProjectMembers(
  projectId: string,
  search = '',
  signal?: AbortSignal,
): Promise<TeamMember[]> {
  const query = search.trim() === '' ? '' : `?search=${encodeURIComponent(search.trim())}`

  return apiGet<TeamMember[]>(`/project/${projectId}/member${query}`, { signal })
}

/**
 * Who `@` can reach on this project.
 *
 * A wider list than the members: assigning work needs somebody on the
 * project, but naming somebody only needs somebody who can read what was
 * written, and on a public board that is everybody.
 */
export function fetchMentionablePeople(
  projectId: string,
  search = '',
  signal?: AbortSignal,
  limit?: number,
): Promise<TeamMember[]> {
  const query = new URLSearchParams()

  if (search.trim() !== '') query.set('search', search.trim())
  if (limit !== undefined) query.set('limit', String(limit))

  const suffix = query.size === 0 ? '' : `?${query.toString()}`

  return apiGet<TeamMember[]>(`/project/${projectId}/mentionable${suffix}`, { signal })
}

export function setProjectMembers(projectId: string, memberIds: string[]): Promise<Board | null> {
  return apiSend<Board>(`/project/${projectId}/member`, 'PUT', { member_ids: memberIds })
}

/**
 * What a project has put away.
 *
 * Not part of the board: archiving is how work leaves the board, so asking
 * for it is a deliberate second request rather than a flag on the first.
 */
/**
 * Which teams this project is shared with, in full.
 *
 * Beside `setProjectMembers` rather than folded into it: the two lists are
 * edited side by side and mean different things.
 */
export function setProjectTeams(projectId: string, teamIds: string[]): Promise<Board | null> {
  return apiSend<Board>(`/project/${projectId}/team`, 'PUT', { team_ids: teamIds })
}

/** Teams a project could be shared with, for the picker. */
export function fetchAssignableTeams(search?: string, signal?: AbortSignal): Promise<ProjectTeam[]> {
  return apiGet<ProjectTeam[]>('/project/assignable-team', {
    query: search ? { search } : {},
    signal,
  })
}

export function fetchArchivedTasks(projectId: string, signal?: AbortSignal): Promise<TaskView[]> {
  return apiGet<TaskView[]>(`/project/${projectId}/archived-task`, { signal })
}

export function createSection(projectId: string, name: string): Promise<Board | null> {
  return apiSend<Board>(`/project/${projectId}/section`, 'POST', { name })
}

/**
 * The project's columns in a new order.
 *
 * The whole order is sent rather than "move this one to index 3": positions
 * are rewritten from the list, so two people dragging at once end with one of
 * the two orders rather than an interleaving of both.
 */
/**
 * The viewer's own tab strip, in a new order.
 *
 * The whole order goes, not "move this one to index 3": positions are
 * rewritten from the list, so two drags in quick succession end with one of
 * the two orders rather than an interleaving of both.
 */
export function reorderPins(orderedProjectIds: string[]): Promise<null> {
  return apiSend<null>('/project/pin-order', 'PUT', {
    ordered_project_ids: orderedProjectIds,
  }) as Promise<null>
}

export function reorderSections(projectId: string, orderedSectionIds: string[]): Promise<null> {
  return apiSend<null>(`/project/${projectId}/section-order`, 'PUT', {
    ordered_section_ids: orderedSectionIds,
  }) as Promise<null>
}

export function renameSection(sectionId: string, name: string): Promise<null> {
  return apiSend<null>(`/section/${sectionId}`, 'PATCH', { name }) as Promise<null>
}

/**
 * The signed-in person's own colour for one column.
 *
 * Null clears it, back to the default card background. Needs only the ability
 * to read the project: this writes nothing about the project itself.
 */
/**
 * A copy of a task, beneath the one it came from.
 *
 * What a copy carries is the server's rule, not the client's: the words, the
 * type, the priority, the due date, the assignee and the checklist, but never
 * the comments, the files or the completed state.
 */
/**
 * Who the task is on behalf of.
 *
 * Unlike assigning, this grants nobody anything: a reporter is a record of
 * who asked for the work, not somebody being given it.
 */
export function setTaskReporter(taskId: string, reporterId: string | null): Promise<null> {
  return apiSend<null>(`/task/${taskId}/reporter`, 'PUT', { reporter_id: reporterId }) as Promise<null>
}

/** Highlights a card for everybody, or clears it with null. */
export function setTaskColour(taskId: string, colour: string | null): Promise<null> {
  return apiSend<null>(`/task/${taskId}/colour`, 'PUT', { colour }) as Promise<null>
}

export function duplicateTask(taskId: string): Promise<{ id: string } | null> {
  return apiSend<{ id: string }>(`/task/${taskId}/copy`, 'POST')
}

export function setSectionColour(sectionId: string, colour: string | null): Promise<null> {
  return apiSend<null>(`/section/${sectionId}/colour`, 'PUT', { colour }) as Promise<null>
}

export function deleteSection(sectionId: string): Promise<null> {
  return apiSend<null>(`/section/${sectionId}`, 'DELETE') as Promise<null>
}

/**
 * Clears every section from a project.
 *
 * Tasks are kept and fall back to the unsectioned list, exactly as when a
 * single section is deleted.
 */
export function deleteAllSections(projectId: string): Promise<Board | null> {
  return apiSend<Board>(`/project/${projectId}/section`, 'DELETE')
}

// ── Tasks ─────────────────────────────────────────────────────────────────

export function fetchMyTasks(overdueOnly = false, limit = 50, signal?: AbortSignal): Promise<TaskView[]> {
  return apiGet<TaskView[]>('/task', { query: { overdue: overdueOnly, limit }, signal })
}

export function createTask(
  projectId: string,
  body: {
    title: string
    type?: TaskKind
    description?: string
    priority?: number
    due_on?: string | null
    assignee_id?: string | null
    section_id?: string | null
  },
): Promise<{ id: string } | null> {
  return apiSend<{ id: string }>(`/project/${projectId}/task`, 'POST', body)
}

export function updateTask(
  taskId: string,
  body: { title: string; type?: TaskKind; description?: string; priority?: number },
): Promise<null> {
  return apiSend<null>(`/task/${taskId}`, 'PATCH', body) as Promise<null>
}

/**
 * Removes a task and everything hanging off it, for good.
 *
 * Archiving is what the interface offers for work that is over; this is the
 * genuine purge, and it needs `project.task.delete` - which nothing but the
 * Administrator role holds unless somebody deliberately grants it.
 */
export function deleteTask(taskId: string): Promise<null> {
  return apiSend<null>(`/task/${taskId}`, 'DELETE') as Promise<null>
}

/**
 * Putting a task away, or bringing it back.
 *
 * What the interface offers where deleting a task used to be: the task leaves
 * the board and the counts, and its comments, files and trail stay.
 */
export function setTaskArchived(taskId: string, archived: boolean): Promise<null> {
  return apiSend(`/task/${taskId}/archive`, 'PUT', { archived })
}

export function setTaskCompletion(taskId: string, completed: boolean): Promise<null> {
  return apiSend<null>(`/task/${taskId}/completion`, 'PUT', { completed }) as Promise<null>
}

export function assignTask(taskId: string, assigneeId: string | null): Promise<null> {
  return apiSend<null>(`/task/${taskId}/assignee`, 'PUT', { assignee_id: assigneeId }) as Promise<null>
}

export function scheduleTask(taskId: string, dueOn: string | null): Promise<null> {
  return apiSend<null>(`/task/${taskId}/schedule`, 'PUT', { due_on: dueOn }) as Promise<null>
}

/**
 * Applies a drag: the target column plus that column's full order.
 *
 * Sending the whole order rather than an index means two people dragging in
 * the same column converge, instead of fighting over a position that meant
 * something different by the time it arrived.
 */
export function moveTask(
  taskId: string,
  sectionId: string | null,
  orderedTaskIds: string[],
): Promise<null> {
  return apiSend<null>(`/task/${taskId}/position`, 'PUT', {
    section_id: sectionId,
    ordered_task_ids: orderedTaskIds,
  }) as Promise<null>
}

// ── Checklists ────────────────────────────────────────────────────────────

/**
 * Checklist calls return nothing useful on their own - the caller refetches
 * the board, which is also what keeps the card's progress badge honest.
 */
export function addChecklistItem(taskId: string, content: string): Promise<{ id: string } | null> {
  return apiSend<{ id: string }>(`/task/${taskId}/checklist`, 'POST', { content })
}

export function updateChecklistItem(itemId: string, content: string): Promise<null> {
  return apiSend<null>(`/checklist/${itemId}`, 'PATCH', { content }) as Promise<null>
}

export function setChecklistItemDone(itemId: string, done: boolean): Promise<null> {
  return apiSend<null>(`/checklist/${itemId}/done`, 'PUT', { done }) as Promise<null>
}

export function deleteChecklistItem(itemId: string): Promise<null> {
  return apiSend<null>(`/checklist/${itemId}`, 'DELETE') as Promise<null>
}

/**
 * Tags matching what was typed, those starting with it first; with nothing
 * typed, the most recently made. Always a small answer, never the whole list.
 */
export function fetchTags(search: string, signal?: AbortSignal): Promise<TagView[]> {
  return apiGet<TagView[]>(`/project/tag?search=${encodeURIComponent(search)}`, { signal })
}

/** Hands back the existing tag when one is already called this, in any case. */
export function createTag(name: string, colour?: string): Promise<TagView | null> {
  return apiSend<TagView>('/project/tag', 'POST', { name, colour })
}

/** A tag as the settings page lists it, with how many tasks carry it. */
export type TagUsage = {
  id: string
  name: string
  colour: string
  task_count: number
  created_at: string
}

export function fetchTagUsage(search: string, page: number, perPage: number, signal?: AbortSignal): Promise<Paginated<TagUsage>> {
  return apiGetPage<TagUsage>(`/project/tag/usage?search=${encodeURIComponent(search)}&page=${page}&per_page=${perPage}`, { signal })
}

export type TaggedTask = {
  id: string
  key: string
  title: string
  project_name: string
  project_colour: string
  completed: boolean
  archived: boolean
}

/**
 * A page of the tasks carrying a tag, on projects this person can open.
 * `elsewhere` counts the ones on projects they cannot, which are never named.
 */
export async function fetchTaggedTasks(
  tagId: string,
  page: number,
  perPage: number,
  signal?: AbortSignal,
): Promise<Paginated<TaggedTask> & { elsewhere: number }> {
  const result = await apiGetPage<TaggedTask>(`/project/tag/${tagId}/task?page=${page}&per_page=${perPage}`, { signal })
  const elsewhere = Number.parseInt(result.headers?.get('X-Elsewhere-Count') ?? '0', 10)

  return { ...result, elsewhere: Number.isFinite(elsewhere) ? elsewhere : 0 }
}

/** Renames and recolours a tag - on every project at once. */
export function editTag(id: string, name: string, colour: string): Promise<null> {
  return apiSend<null>(`/project/tag/${id}`, 'PATCH', { name, colour }) as Promise<null>
}

/** Deletes a tag, and with it the tag on every task that carried it. */
export function deleteTag(id: string): Promise<null> {
  return apiSend<null>(`/project/tag/${id}`, 'DELETE') as Promise<null>
}

/** Replaces the task's tags with this set, in this order. */
export function setTaskTags(taskId: string, tagIds: string[]): Promise<null> {
  return apiSend<null>(`/task/${taskId}/tag`, 'PUT', { tag_ids: tagIds }) as Promise<null>
}

export function reorderChecklist(taskId: string, orderedItemIds: string[]): Promise<null> {
  return apiSend<null>(`/task/${taskId}/checklist-order`, 'PUT', {
    ordered_item_ids: orderedItemIds,
  }) as Promise<null>
}

export type TaskSearchResult = {
  id: string
  key: string
  title: string
  type: TaskKind
  completed: boolean
  project_name: string
  project_colour: string
}

export type RelatedTask = {
  /** What "remove" acts on. The link is the record, not the task. */
  link_id: string
  task_id: string
  /** WEB-42: what the link in the UI points at. */
  key: string
  title: string
  type: TaskKind
  completed: boolean
  project_id: string
  project_name: string
  project_colour: string
}

/**
 * Relating is symmetric, so these read and write the same set whichever task
 * the person happens to be looking at.
 */
export function fetchTaskLinks(taskId: string, signal?: AbortSignal): Promise<RelatedTask[]> {
  return apiGet<RelatedTask[]>(`/task/${taskId}/link`, { signal })
}

export function relateTask(taskId: string, relatedTaskId: string): Promise<RelatedTask[] | null> {
  return apiSend<RelatedTask[]>(`/task/${taskId}/link`, 'POST', { related_task_id: relatedTaskId })
}

export function unrelateTask(linkId: string): Promise<null> {
  return apiSend<null>(`/task/link/${linkId}`, 'DELETE') as Promise<null>
}

/** Scoped server-side to projects the viewer can already open. */
export function searchTasks(
  search: string,
  excludeTaskId?: string,
  signal?: AbortSignal,
  limit = 8,
): Promise<TaskSearchResult[]> {
  return apiGet<TaskSearchResult[]>('/task/search', {
    query: {
      search,
      ...(excludeTaskId ? { exclude: excludeTaskId } : {}),
      limit: String(limit),
    },
    signal,
  })
}

/**
 * The tasks named inside a body of rich text, as they stand now.
 *
 * Bodies store ids rather than keys, because a key is derived from its
 * project's code and renaming that code renames every key at once. So what a
 * reference displays is resolved at render time and is never stale.
 */
export function resolveTaskReferences(
  ids: string[],
  signal?: AbortSignal,
): Promise<TaskSearchResult[]> {
  if (ids.length === 0) return Promise.resolve([])

  return apiGet<TaskSearchResult[]>('/task/reference', {
    query: { ids: ids.join(',') },
    signal,
  })
}

/**
 * Pin a project to the tab strip, or unpin it.
 *
 * State rather than a toggle, so a double-click cannot flip it twice. The
 * refreshed list comes back, because the tab strip is built from it.
 */
export function setProjectPinned(projectId: string, pinned: boolean): Promise<ProjectView[] | null> {
  return apiSend<ProjectView[]>(`/project/${projectId}/pin`, 'PUT', { pinned })
}

/** How the work is going: throughput, and the shape of what is left. */
export function fetchProjectReport(weeks = 8, signal?: AbortSignal): Promise<ProjectReport> {
  return apiGet<ProjectReport>('/project/report', { query: { weeks }, signal })
}
